import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { Plus, Users, Trash2, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClassesPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [creating, setCreating] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchClasses();
  }, [token]);

  const fetchClasses = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/classes`, { headers });
      if (res.ok) setClasses(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const createClass = async () => {
    if (!newClassName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/classes`, { method: 'POST', headers, body: JSON.stringify({ name: newClassName.trim(), students: [] }) });
      if (res.ok) { setNewClassName(''); setShowCreate(false); fetchClasses(); }
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const deleteClass = async (id) => {
    if (!window.confirm(t('confirmDelete', language))) return;
    try {
      await fetch(`${BACKEND_URL}/api/classes/${id}`, { method: 'DELETE', headers });
      fetchClasses();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{t('classManagement', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <div className="dashboard-intro">
          <h2 className="dashboard-title">{t('yourClasses', language)}</h2>
          <p className="dashboard-desc">{t('classesDesc', language)}</p>
        </div>

        <button className="class-create-btn" onClick={() => setShowCreate(true)} data-testid="create-class-btn">
          <Plus size={20} /> {t('createClass', language)}
        </button>

        {showCreate && (
          <motion.div className="class-create-form" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <input className="setup-input setup-input-team1" type="text" value={newClassName}
              onChange={e => setNewClassName(e.target.value)} placeholder={t('classNamePlaceholder', language)}
              maxLength={30} data-testid="class-name-input" />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="auth-submit" style={{ flex: 1 }} onClick={createClass} disabled={creating}>
                {creating ? '...' : t('create', language)}
              </button>
              <button className="upgrade-btn-secondary" onClick={() => setShowCreate(false)}>{t('cancel', language)}</button>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="lb-loading"><div className="lb-spinner" /></div>
        ) : classes.length === 0 ? (
          <div className="lb-empty">{t('noClasses', language)}</div>
        ) : (
          <div className="classes-list">
            {classes.map((cls) => (
              <div key={cls._id} className="class-card" data-testid={`class-card-${cls._id}`}>
                <div className="class-card-main" onClick={() => navigate(`/classes/${cls._id}`)}>
                  <div className="class-card-icon"><Users size={22} /></div>
                  <div className="class-card-info">
                    <h3 className="class-card-name">{cls.name}</h3>
                    <p className="class-card-count">{(cls.students || []).length} {t('studentsCount', language)}</p>
                  </div>
                  <ChevronRight size={20} className="class-card-arrow" />
                </div>
                <button className="class-delete-btn" onClick={() => deleteClass(cls._id)}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}

        <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '16px' }}>&larr; {t('backToHome', language)}</button>
      </div>
    </div>
  );
}
